import { t } from '@transifex/native';

import {
	errorMessage,
	trackTransition,
} from 'pkg/actions/form_event_group_waitlist';
import type {
	AuthedCreateSubmissionProps,
	AuthenticatedSubmissionCreatePayload,
	BasicCreateSubmissionProps,
	BasicSubmissionCreatePayload,
	CreateFormSubmissionPayloadParams,
	CreateFormSubmissionResponse,
	SubmissionAgreementPayload,
} from 'pkg/actions/form_submissions/interface';
import type {
	PaymentPreviewPayload,
	PaymentRow,
} from 'pkg/actions/payment_preview';

import * as actions from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create as modelsCreate } from 'pkg/api/models/actions';
import { computeContentFingerprint } from 'pkg/api/models/agreement/utils';
import * as sdk from 'pkg/core/sdk';
import { isApp } from 'pkg/platform';

import type { RegistrationFormContextProps } from 'routes/forms/submission/form/context';
import { AgreementConsent } from 'routes/forms/submission/form/context';
import type { FormFieldV2 } from 'routes/forms/submission/form/interface';
import type {
	Bundle,
	ProductWithQty,
} from 'routes/forms/submission/form/registration_form/getBundlesAndProducts';
import {
	convertFormDataToPayload,
	getEventGroupPaymentRows,
} from 'routes/forms/submission/form/utils';

import { Event, stopTimedTrackEvent } from 'components/analytics';

import { asString, type FormPayload } from 'components/form/Form';

import * as flashes from '../flashes';

export const fetchBundlesData = async (
	form: models.form.Form,
	bundles: { [key: number]: Bundle },
	products: ProductWithQty[],
	registrationCtx: RegistrationFormContextProps,
	productQtys: {
		[key: number]: number;
	}
) => {
	if (form.id && form.productPrices?.length) {
		const eventGroupRows = getEventGroupPaymentRows(
			form,
			registrationCtx.state.selectedEventGroupIds,
			registrationCtx.state.chosenFormEventIds
		);

		const payload: PaymentPreviewPayload[] = Object.values(bundles).map(
			(bundle) => {
				const preview: PaymentPreviewPayload = {
					groupId: form?.groupId,
					bundleNumber: bundle.bundleNumber,
					rows: bundle.productPrices.map((pp) => {
						const product = products.find((prod) => prod.id === pp.productId);

						const formProductPrice = form.productPrices.find(
							(fpp) =>
								fpp.bundleNumber == registrationCtx.state.activeBundleNumber &&
								fpp.productPriceId == pp.id
						);

						const row: PaymentRow = {
							productPriceId: pp.id,
							quantity: productQtys[product?.id],
						};

						if (formProductPrice?.taxRateId) {
							row.taxRateId = formProductPrice.taxRateId;
						}

						return row;
					}),
				};

				preview.rows.push(...eventGroupRows);

				if (bundle.subscriptionStartsAt) {
					preview.subscriptionStartAt = bundle.subscriptionStartsAt;
					preview.delayedSubscriptionStart = true;
				}

				if (bundle.installmentCount) {
					preview.installmentCount = bundle.installmentCount;
				}

				if (registrationCtx.state.discount) {
					preview.discountId = registrationCtx.state.discount.id;
				}

				return preview;
			}
		);

		const [req, resp] =
			await actions.paymentPreview.getBundlesPreviews(payload);

		if (req) {
			registrationCtx.setState({
				bundlePreviews: resp,
			});
		}
	}
};

export const fetchCartData = async (
	form: models.form.Form,
	bundles: { [key: number]: Bundle },
	products: ProductWithQty[],
	registrationCtx: RegistrationFormContextProps,
	productQtys: { [key: number]: number }
) => {
	if (form?.groupId && form.productPrices?.length) {
		const activeBundle = bundles[registrationCtx.state.activeBundleNumber];

		const rows: PaymentRow[] = activeBundle.productPrices.map((pp) => {
			const product = products.find((prod) => prod.id === pp.productId);

			const formProductPrice = form.productPrices.find(
				(fpp) =>
					fpp.bundleNumber == registrationCtx.state.activeBundleNumber &&
					fpp.productPriceId == pp.id
			);

			const row: PaymentRow = {
				productPriceId: pp.id,
				quantity: productQtys[product?.id],
			};

			if (formProductPrice?.taxRateId) {
				row.taxRateId = formProductPrice.taxRateId;
			}

			return row;
		});

		rows.push(
			...getEventGroupPaymentRows(
				form,
				registrationCtx.state.selectedEventGroupIds,
				registrationCtx.state.chosenFormEventIds
			)
		);

		const payload: PaymentPreviewPayload = {
			groupId: form.groupId,
			rows,
			bundleNumber: registrationCtx.state.activeBundleNumber,
		};

		if (activeBundle.subscriptionStartsAt) {
			payload.subscriptionStartAt = activeBundle.subscriptionStartsAt;
			payload.delayedSubscriptionStart = true;
		}

		if (activeBundle.installmentCount) {
			payload.installmentCount = activeBundle.installmentCount;
		}

		if (registrationCtx.state.discount) {
			payload.discountId = registrationCtx.state.discount.id;
		}

		const [req, resp] = await actions.paymentPreview.getPreview(payload);

		if (req) {
			registrationCtx.setState({
				preview: resp,
			});
		}
	}
};

// The cart for a form whose only payment is the selected priced event groups.
// These forms have no products or packages, so the activity cart step (and
// Billing after it) fetch this preview themselves, including any discount the
// submitter applied — without it the summary would show an undiscounted total.
export const fetchEventGroupCartData = async (
	form: models.form.Form,
	registrationCtx: RegistrationFormContextProps
) => {
	const eventGroupRows = getEventGroupPaymentRows(
		form,
		registrationCtx.state.selectedEventGroupIds,
		registrationCtx.state.chosenFormEventIds
	);

	if (!form?.groupId || eventGroupRows.length === 0) {
		return;
	}

	const payload: PaymentPreviewPayload = {
		groupId: form.groupId,
		rows: eventGroupRows,
	};

	if (registrationCtx.state.discount) {
		payload.discountId = registrationCtx.state.discount.id;
	}

	const [req, resp] = await actions.paymentPreview.getPreview(payload);

	if (req) {
		registrationCtx.setState({
			preview: resp,
		});
	}
};

interface IneligibleEventGroupsResponse {
	ineligibleEventGroupIds: number[];
}

export async function fetchIneligibleEventGroupIds(
	form: models.form.Form,
	formFields: FormFieldV2[],
	data: FormPayload,
	submittedForAccount: models.account.Account
): Promise<[boolean, number[]]> {
	const { values, optionIds } = convertFormDataToPayload(
		data,
		formFields,
		form
	);

	const resp = await sdk.post(
		endpoints.FormSubmissions.ListIneligibleEventGroups(form.guid),
		{},
		{ values, optionIds, submittingForAccountId: submittedForAccount.id }
	);

	if (!resp.ok) {
		return [false, []];
	}

	const json: IneligibleEventGroupsResponse = await resp.json();

	return [true, json.ineligibleEventGroupIds ?? []];
}

interface JoinWaitlistResponse {
	entries?: models.formEventGroupWaitlistEntry.FormEventGroupWaitlistEntry[];
}

export async function joinWaitlist(
	form: models.form.Form,
	submittedForAccount: models.account.Account,
	eventGroupIds: number[]
): Promise<[boolean, string]> {
	const resp = await sdk.post(
		endpoints.FormSubmissions.JoinWaitlist(form.guid),
		{},
		{ submittingForAccountId: submittedForAccount.id, eventGroupIds }
	);

	if (resp.ok) {
		const body: JoinWaitlistResponse = await resp.json().catch(() => ({}));
		const userIds = [
			...new Set((body.entries ?? []).map((entry) => entry.submittedForUserId)),
		];

		trackTransition(Event.WaitlistJoined, form.groupId, form.id, {
			eventGroupIds,
			userIds,
		});

		return [true, ''];
	}

	return [false, await errorMessage(resp)];
}

interface WaitlistEntryResponse {
	entry?: models.formEventGroupWaitlistEntry.FormEventGroupWaitlistEntry;
}

async function changeWaitlistEntry(
	endpoint: string,
	event: Event,
	form: models.form.Form,
	submittedForAccount: models.account.Account,
	eventGroupId: number
): Promise<[boolean, string]> {
	const resp = await sdk.post(
		endpoint,
		{},
		{ submittingForAccountId: submittedForAccount.id, eventGroupId }
	);

	if (!resp.ok) {
		return [false, await errorMessage(resp)];
	}

	const body: WaitlistEntryResponse = await resp.json().catch(() => ({}));

	trackTransition(event, form.groupId, form.id, {
		eventGroupIds: [eventGroupId],
		userIds: body.entry ? [body.entry.submittedForUserId] : [],
	});

	return [true, ''];
}

export async function leaveWaitlist(
	form: models.form.Form,
	submittedForAccount: models.account.Account,
	eventGroupId: number
): Promise<[boolean, string]> {
	return changeWaitlistEntry(
		endpoints.FormSubmissions.LeaveWaitlist(form.guid),
		Event.WaitlistLeft,
		form,
		submittedForAccount,
		eventGroupId
	);
}

export async function declineWaitlistInvite(
	form: models.form.Form,
	submittedForAccount: models.account.Account,
	eventGroupId: number
): Promise<[boolean, string]> {
	return changeWaitlistEntry(
		endpoints.FormSubmissions.DeclineWaitlistInvite(form.guid),
		Event.WaitlistInviteDeclined,
		form,
		submittedForAccount,
		eventGroupId
	);
}

export async function removeFromEventGroup(
	organizationId: number,
	formId: number,
	eventGroupId: number,
	submissionId: number
): Promise<boolean> {
	const failureTitle = t("Couldn't remove them from the program");

	try {
		const resp = await sdk.destroy(
			endpoints.FormEventGroups.RemoveParticipant(
				organizationId,
				formId,
				eventGroupId,
				submissionId
			)
		);

		if (!resp.ok) {
			flashes.show({ title: failureTitle, message: await errorMessage(resp) });
		}

		return resp.ok;
	} catch {
		flashes.show({ title: failureTitle, message: t('Please try again.') });

		return false;
	}
}

interface Props {
	groupId: number;
	selectedFormPackage: models.formPackage.FormPackage;
	selectedPaymentOptionId: number;
	discountId: number;
	formPackageProductsValues: { [key: number]: string };
	extraRows?: PaymentRow[];
}

export const fetchCartDataForPackages = async ({
	groupId,
	selectedFormPackage,
	selectedPaymentOptionId,
	discountId,
	formPackageProductsValues,
	extraRows = [],
}: Props) => {
	if (!selectedFormPackage) {
		return null;
	}

	const paymentOption = selectedFormPackage.paymentOptions.find(
		(po) => po.id === selectedPaymentOptionId
	);

	if (!paymentOption) {
		return null;
	}

	const payload: PaymentPreviewPayload = {
		groupId,
		paymentOptionId: selectedPaymentOptionId,
		rows: [
			...paymentOption.prices.map((price) => {
				const productId = price.productPrice.productId;
				const formPackageProduct = selectedFormPackage.products.find(
					(fpp) => fpp.productId === productId
				);

				return {
					quantity: Number.parseInt(
						formPackageProductsValues[formPackageProduct.id] || '0',
						10
					),
					productPriceId: price.productPriceId,
					taxRateId: formPackageProduct?.taxRateId,
				};
			}),
			...extraRows,
		],
	};

	if (paymentOption.installmentCount) {
		payload.installmentCount = paymentOption.installmentCount;
	}

	if (paymentOption.subscriptionStartAt) {
		payload.subscriptionStartAt = paymentOption.subscriptionStartAt;
		payload.delayedSubscriptionStart = true;
	}

	if (discountId) {
		payload.discountId = discountId;
	}

	const [req, resp] = await actions.paymentPreview.getPreview(payload);

	if (req) {
		return resp;
	}

	return null;
};

/**
 * Maps the agreement consents/signatures collected in the submission flow into
 * the submit payload. Only agreements the submitter actually actioned are
 * included. An accepted agreement carries the typed signature and a content
 * fingerprint over the title/content the submitter saw — the backend rejects a
 * mismatch with `agreement_drifted`, so the submitter re-confirms the new text.
 */
const getAgreementsPayload = (
	form: models.form.Form,
	agreementConsents: CreateFormSubmissionPayloadParams['agreementConsents'],
	agreementSignatures: CreateFormSubmissionPayloadParams['agreementSignatures']
): Promise<SubmissionAgreementPayload[]> =>
	Promise.all(
		(form.formAgreements ?? [])
			.filter((link) => !!agreementConsents[link.agreementId])
			.map(async (link) => {
				const status = agreementConsents[link.agreementId];

				const entry: SubmissionAgreementPayload = {
					agreementId: link.agreementId,
					status,
					signedByName:
						agreementSignatures[link.agreementId]?.trim() || undefined,
				};

				if (status === AgreementConsent.Accepted) {
					entry.contentFingerprint =
						await computeContentFingerprint(
							link.agreement?.title ?? '',
							link.agreement?.content ?? ''
						);
				}

				return entry;
			})
	);

const getCreateSubmissionPayload = async ({
	form,
	fields,
	data,
	discount,
	agreementConsents,
	agreementSignatures,
}: CreateFormSubmissionPayloadParams): Promise<BasicSubmissionCreatePayload> => {
	const hasProducts = form.productPrices?.length > 0;
	const hasPackages = form.packages?.length > 0;

	const converted = convertFormDataToPayload(data, fields, form);

	const payload: BasicSubmissionCreatePayload = {
		...converted,
		useCheckoutService: isApp(),
	};

	const requiresBilling =
		hasProducts || (hasPackages && !!converted.paymentOptionId);

	if (requiresBilling) {
		payload.billingDetails = {
			firstName: asString(data.FirstName),
			lastName: asString(data.LastName),
			email: asString(data.Email),
		};
	}

	if (discount) {
		payload.discountId = discount.id;
		payload.discountCode = discount.code;
	}

	const agreements = await getAgreementsPayload(
		form,
		agreementConsents,
		agreementSignatures
	);

	if (agreements.length) {
		payload.agreements = agreements;
	}

	return payload;
};

export const create = async ({
	form,
	data,
	group,
	fields,
	submittedForAccount,
	discount,
	agreementConsents,
	agreementSignatures,
}: AuthedCreateSubmissionProps): Promise<
	[Response, CreateFormSubmissionResponse, models.APIError?]
> => {
	const payload: AuthenticatedSubmissionCreatePayload = {
		...(await getCreateSubmissionPayload({
			form,
			data,
			fields,
			discount,
			group,
			agreementConsents,
			agreementSignatures,
		})),
		submittedForAccountID: submittedForAccount.id,
	};

	const [request, response, error] = await modelsCreate<
		AuthenticatedSubmissionCreatePayload,
		CreateFormSubmissionResponse
	>(endpoints.FormSubmissions.Create(form.guid), payload);

	if (!request.ok) {
		flashes.show({
			title: t('Failed to submit form submission'),
		});
	}

	// stops the timed tracked from when the user opens the form
	stopTimedTrackEvent(Event.TimedFormSubmissionSubmit, {
		formId: form.id,
		has_products: form.productPrices?.length > 0,
	});

	return [request, response, error];
};

// Public form submission creation (for unauthenticated users)
export async function publicCreate({
	form,
	data,
	group,
	fields,
	discount,
	captchaToken,
	agreementConsents,
	agreementSignatures,
}: BasicCreateSubmissionProps & { captchaToken?: string }): Promise<
	[Response, CreateFormSubmissionResponse, models.APIError?]
> {
	const payload = await getCreateSubmissionPayload({
		form,
		data,
		fields,
		discount,
		group,
		agreementConsents,
		agreementSignatures,
	});

	if (captchaToken) {
		payload.captchaToken = captchaToken;
	}

	const [request, response, error] = await modelsCreate<
		BasicSubmissionCreatePayload,
		CreateFormSubmissionResponse
	>(endpoints.FormSubmissions.PublicCreate(form.guid), payload);

	if (!request.ok) {
		flashes.show({
			title: t('Failed to submit form submission'),
		});
	}

	// stops the timed tracked from when the user opens the form
	stopTimedTrackEvent(Event.TimedFormSubmissionSubmit, {
		formId: form.id,
		has_products: form.productPrices?.length > 0,
	});

	return [request, response, error];
}
